import type { FC } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import type { MarketplaceProperty } from 'components/marketplace/property/types';
import { useTouring } from 'components/self-tour/hooks/useTouring';

import { SelfTourDialog } from '../PropertySection/SelfTourDialog';

import styles from './PdpMobileTourBanner.module.css';

type PdpMobileTourBannerProps = {
  property: MarketplaceProperty;
  onCopyLink: () => void;
};

export const PdpMobileTourBanner: FC<PdpMobileTourBannerProps> = ({ property, onCopyLink }) => {
  const touring = useTouring(property, { placement: 'footer' });
  const isSelfTour = touring?.tourKind === 'selfTour';
  const selfTourAction = isSelfTour ? (touring.secondaryAction ?? touring.primaryAction) : null;

  return (
    <>
      <Box
        role="region"
        aria-label={isSelfTour ? 'Book a self tour' : 'Tour with agent'}
        className={styles.shell}
        data-pdp-mobile-tour-banner
      >
        <Box className={styles.glassSurface}>
          <Row className="h-14 w-full items-center gap-2 px-5">
            <Col className="min-w-0 flex-1 gap-0.5">
              <Text as="p" className="truncate text-label-md text-od-content-primary">
                {isSelfTour ? 'Book a self tour' : 'Tour with agent'}
              </Text>
              <Text as="p" className="truncate text-body-sm text-od-content-secondary">
                {isSelfTour ? 'Available daily 8am–8pm' : 'Share with your agent for booking'}
              </Text>
            </Col>
            <Button
              analyticsName={
                selfTourAction?.analyticsName ?? 'cosmos-marketplace-pdp-mobile-copy-link'
              }
              aria-label={isSelfTour ? 'Book a self tour' : 'Copy listing link'}
              variant="primary"
              size="md"
              onClick={selfTourAction?.onClick ?? onCopyLink}
              disabled={selfTourAction?.disabled}
              className="h-10 shrink-0 rounded-full px-5 text-label-md"
            >
              {isSelfTour ? 'Book tour' : 'Copy'}
            </Button>
          </Row>
        </Box>
      </Box>
      {isSelfTour && touring && (
        <SelfTourDialog
          isOpen={touring.isSelfTourDialogOpen}
          onClose={touring.closeSelfTourDialog}
          onOpenRequest={touring.openSelfTourDialog}
          placement="footer"
          addressToken={property.addressToken}
          slug={property.slug}
          tourBookingListingId={property.tourBookingListingId}
          addressLine={[property.street, property.city].filter(Boolean).join(', ')}
        />
      )}
    </>
  );
};
